import React from 'react';
import { useSelector } from 'react-redux';
import { Link, Text } from 'components/ui';
import StarRating from 'components/StarRating/StarRating';
import localeUtils from 'utils/LanguageLocale';
import { wrapFunctionalComponent } from 'utils/framework';
import numberUtils from 'utils/Number';
import * as pdpAnalytics from 'analytics/productPage/pdpAnalytics';
import anaConsts from 'analytics/constants';
import UI from 'utils/UI';
import mediaUtils from 'utils/Media';
import ReviewsAnchorDropDown from 'components/ProductPage/ReviewsAnchor/ReviewsAnchorDropDown/ReviewsAnchorDropDown.f.jsx';
import helpersUtils from 'utils/Helpers.js';
import ReviewsAnchorEN from 'components/ProductPage/ReviewsAnchor/locales/ReviewsAnchor_en_US';
import ReviewsAnchorFR from 'components/ProductPage/ReviewsAnchor/locales/ReviewsAnchor_fr_CA';
import { getRecommendedPercentage, shouldShowRecommendedPercentage } from 'utils/recommendedPercentage';
import { showPDPPercentRecommendedSelector } from 'viewModel/selectors/testTarget/showPDPPercentRecommendedSelector';
import {
    colors, fontWeights, mediaQueries, fontSizes
} from 'style/config';

const { Media } = mediaUtils;
const { deferTaskExecution } = helpersUtils;

const { getLocalisedDataFromResource } = localeUtils;
const { formatReviewCount } = numberUtils;

const ReviewsAnchor = props => {
    const getText = getLocalisedDataFromResource(ReviewsAnchorEN, ReviewsAnchorFR);

    const buildJumplinkHandler = actionInfo => e => {
        e.preventDefault();
        deferTaskExecution(() => {
            pdpAnalytics.trackLinkClick({
                eventStrings: ['event71'],
                linkName: 'D=c55',
                actionInfo
            });
            UI.scrollTo({ elementId: 'ratings-reviews-container' });
        });
    };

    const clickHandler = buildJumplinkHandler(anaConsts.ACTION_INFO.JUMPLINK_RATINGS_AND_REVIEWS);
    const recommendedClickHandler = buildJumplinkHandler(anaConsts.ACTION_INFO.JUMPLINK_RATINGS_AND_REVIEWS_RECOMMEND);

    const {
        rating, showAddDropDownReviewSection, reviewStatistics, initialTotalReviews, showProsAndCons, currentProduct, showPDPTopOfPageRedesign
    } =
        props;
    let { reviews } = props;
    reviews = reviews === undefined ? 0 : reviews;
    const formattedReviewCount = formatReviewCount(reviews);
    const showPDPPercentRecommended = useSelector(showPDPPercentRecommendedSelector);
    const recommendedPercentage = getRecommendedPercentage(initialTotalReviews, reviewStatistics?.recommendedCount);
    const showRecommendedPercentage =
        showPDPTopOfPageRedesign && showPDPPercentRecommended && reviews > 0 && shouldShowRecommendedPercentage(recommendedPercentage);

    const anchorContent =
        reviews > 0 ? (
            <React.Fragment>
                {showPDPTopOfPageRedesign && (
                    <span
                        css={[styles.count, styles.ratingNumber, styles.topOfPageRedesignText]}
                        children={rating?.toFixed(1)}
                    />
                )}
                <StarRating rating={rating} />
                <span
                    data-at={Sephora.debug.dataAt('number_of_reviews')}
                    css={[
                        styles.count,
                        !showPDPTopOfPageRedesign && styles.textOffset,
                        showPDPTopOfPageRedesign && styles.reviewsLinkRedesign,
                        showPDPTopOfPageRedesign && styles.topOfPageRedesignText
                    ]}
                    children={
                        showPDPTopOfPageRedesign
                            ? `(${getText('reviewsLabel', [formattedReviewCount, reviews])})`
                            : getText('reviewsLabel', [formattedReviewCount, reviews])
                    }
                />
            </React.Fragment>
        ) : (
            <span css={styles.textOffset}>{getText(showPDPTopOfPageRedesign ? 'writeReviewCapitalized' : 'writeReview')}</span>
        );

    const recommendedContent = showRecommendedPercentage && (
        <Text css={[styles.count, styles.textOffset, styles.recommended]}>
            <Text
                marginLeft={1}
                marginRight={2}
                css={props.pdpRedesignDividerStyle}
            >
                |
            </Text>
            <Link
                href='#ratings-reviews-container'
                onClick={recommendedClickHandler}
                color='inherit'
                style={{ textDecoration: 'none' }}
            >
                <b>{recommendedPercentage}%</b> {getText('recommended')}
            </Link>
        </Text>
    );

    const dropdownContent =
        showAddDropDownReviewSection && reviews > 0 ? (
            <ReviewsAnchorDropDown
                reviews={reviews}
                rating={rating}
                formattedReviewCount={formattedReviewCount}
                reviewStatistics={reviewStatistics}
                initialTotalReviews={initialTotalReviews}
                showProsAndCons={showProsAndCons}
                currentProduct={currentProduct}
                clickHandler={clickHandler}
            />
        ) : null;

    const linkColor = showPDPTopOfPageRedesign ? 'var(--color-blue)' : 'inherit';

    return (
        <React.Fragment>
            <Media greaterThan='xs'>
                <div css={styles.wrapper}>
                    <Link
                        href='#ratings-reviews-container'
                        onClick={clickHandler}
                        display='flex'
                        alignItems='center'
                        color={linkColor}
                        style={{ textDecoration: showPDPTopOfPageRedesign ? 'none' : 'underline' }}
                    >
                        {anchorContent}
                    </Link>
                    {recommendedContent}
                    {dropdownContent}
                </div>
            </Media>
            <Media at='xs'>
                <div css={styles.wrapper}>
                    <Link
                        href='#ratings-reviews-container'
                        onClick={clickHandler}
                        display='flex'
                        alignItems='center'
                        color={linkColor}
                        style={{ textDecoration: showPDPTopOfPageRedesign ? 'none' : 'underline' }}
                    >
                        {anchorContent}
                    </Link>
                    {recommendedContent}
                    {dropdownContent}
                </div>
            </Media>
        </React.Fragment>
    );
};

const styles = {
    wrapper: {
        display: 'flex',
        alignItems: 'center'
    },
    count: {
        fontWeight: 'var(--font-weight-bold)',
        marginLeft: '.4em'
    },
    textOffset: {
        position: 'relative',
        top: '.09em'
    },
    ratingNumber: {
        marginLeft: 0,
        marginRight: '.4em',
        color: colors.black
    },
    reviewsLinkRedesign: {
        color: colors.blue,
        fontWeight: fontWeights.normal,
        '&:hover': {
            textDecoration: 'underline'
        }
    },
    recommended: {
        color: colors.black,
        fontWeight: fontWeights.normal,
        fontSize: fontSizes.sm,
        [mediaQueries.sm]: {
            fontSize: fontSizes.base
        }
    },
    topOfPageRedesignText: {
        fontSize: fontSizes['sm-bg'],
        [mediaQueries.sm]: {
            fontSize: fontSizes['base-bg']
        }
    }
};

export default wrapFunctionalComponent(ReviewsAnchor, 'ReviewsAnchor');
